import * as React from 'react';
import {Formik} from 'formik';
import * as styled from './styled';
import Container from 'containers/Container';
import {Header, Footer} from './components';
import {BuilderProps, BuilderValue, ChildrenOptions} from './types';

/**
 * The component create builder.
 * @param {BuilderProps} props
 * @return {React.ReactElement<BuilderProps>}
 * @constructor
 */
function Builder<T extends object>(
    props: BuilderProps<T>,
): React.ReactElement<BuilderProps<T>> {
  const initialValues: Partial<BuilderValue<T>>
    = props.value || props.defaultValue || {};

  /**
   * Is the instance created.
   * @return {boolean}
   */
  function isCreated(): boolean {
    return Boolean(initialValues.id);
  }

  /**
   * Get children options.
   * @return {ChildrenOptions}
   */
  function getChildrenOptions(): ChildrenOptions {
    return {
      isCreated,
    };
  }

  /**
   * The submit event handler.
   * @param {Partial<BuilderValue<T>>} value
   */
  function handleSubmit(value: Partial<BuilderValue<T>>): void {
    if (isCreated()) {
      props.onUpdate && (
        props.onUpdate(value as BuilderValue<T>)
      );
    } else {
      props.onCreate && (
        props.onCreate(value as BuilderValue<T>)
      );
    }
  }

  /**
   * Render header.
   * @return {JSX.Element | JSX.Element[] | undefined}
   */
  function renderHeader() {
    return props.children.header ? (
      props.children.header(
        getChildrenOptions()
      )
    ) : undefined;
  }

  /**
   * Render footer.
   * @return {JSX.Element | JSX.Element[] | undefined}
   */
  function renderFooter() {
    return props.children.footer ? (
      props.children.footer(
        getChildrenOptions()
      )
    ) : undefined;
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {() => (
        <styled.Wrapper>
          <Container>
            {renderHeader()}
            {props.children.content}
          </Container>
          {renderFooter()}
        </styled.Wrapper>
      )}
    </Formik>
  );
}

Builder.Header = Header;
Builder.Footer = Footer;

export default Builder;
